import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Loader2, Monitor, Moon, RotateCcw, Save, Send, Smartphone, Sun } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { apiClient } from '../../config/api'
import {
  apiErrorMessage,
  communicationsService,
  emailIsValid
} from '../../services/communicationsService'
import MailBlockEditor from './MailBlockEditor'
import type {
  EmailTemplate, Locale, MailLayout, TemplateCatalog, TemplatePreview, TemplateVariable
} from '../../types/communications'

interface Props {
  notify?: (message: string, type?: 'success' | 'error' | 'warning' | 'info') => void
}

const CATEGORY_ORDER = ['ACCOUNT', 'SECURITY', 'ALERT', 'NEWSLETTER', 'SYSTEM']

const EmailTemplateManager: React.FC<Props> = ({ notify }) => {
  const { t, i18n } = useTranslation()
  const uiLocale = (i18n.language?.startsWith('fr') ? 'fr' : 'en') as Locale
  const [catalog, setCatalog] = useState<TemplateCatalog | null>(null)
  const [selectedKey, setSelectedKey] = useState('')
  const [locale, setLocale] = useState<Locale>(uiLocale)
  const [drafts, setDrafts] = useState<Record<string, EmailTemplate>>({})
  const [dirty, setDirty] = useState<Record<string, boolean>>({})
  const [preview, setPreview] = useState<TemplatePreview | null>(null)
  const [previewMode, setPreviewMode] = useState<'html' | 'text'>('html')
  const [mobile, setMobile] = useState(false)
  const [dark, setDark] = useState(false)
  const [recipient, setRecipient] = useState('')
  const [recipientOptions, setRecipientOptions] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState('')
  const [previewing, setPreviewing] = useState(false)

  const draftId = `${selectedKey}:${locale}`
  const draft = drafts[draftId]
  const variables: TemplateVariable[] = catalog?.variables[draftId] || []
  const layouts: MailLayout[] = catalog?.layouts || []

  const load = async () => {
    setLoading(true)
    try {
      const next = await communicationsService.listTemplates()
      setCatalog(next)
      setDrafts(Object.fromEntries(next.templates.map(row => [`${row.templateKey}:${row.locale}`, { ...row }])))
      setSelectedKey(current => current || next.templates[0]?.templateKey || '')
    } catch (error) {
      notify?.(apiErrorMessage(error, t('communications.templates.loadFailed')), 'error')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    apiClient.get('/users').then(response => {
      const users = Array.isArray(response.data) ? response.data : response.data?.users || []
      setRecipientOptions([...new Set(users.map((user: any) => String(user.email || '')).filter(emailIsValid))] as string[])
    }).catch(() => setRecipientOptions([]))
  }, [])

  // The preview follows the draft, so an edit is visible without saving first.
  const previewTimer = useRef<number>()
  useEffect(() => {
    if (!draft) return
    window.clearTimeout(previewTimer.current)
    previewTimer.current = window.setTimeout(async () => {
      setPreviewing(true)
      try {
        setPreview(await communicationsService.previewTemplate(draft, undefined,
          dark ? { theme: 'DARK' } : { theme: 'LIGHT' }))
      } catch (error) {
        notify?.(apiErrorMessage(error, t('communications.templates.previewFailed')), 'error')
      } finally {
        setPreviewing(false)
      }
    }, 450)
    return () => window.clearTimeout(previewTimer.current)
  }, [draft, dark])

  const groups = useMemo(() => {
    const unique = new Map<string, EmailTemplate>()
    for (const template of catalog?.templates || []) {
      if (!unique.has(template.templateKey)) unique.set(template.templateKey, template)
    }
    return CATEGORY_ORDER
      .map(category => ({
        category,
        items: [...unique.values()].filter(template => template.category === category)
      }))
      .filter(group => group.items.length > 0)
  }, [catalog])

  const update = <K extends keyof EmailTemplate>(field: K, value: EmailTemplate[K]) => {
    setDrafts(current => ({ ...current, [draftId]: { ...current[draftId], [field]: value } }))
    setDirty(current => ({ ...current, [draftId]: true }))
  }

  const save = async () => {
    if (!draft) return
    setBusy('save')
    try {
      const saved = await communicationsService.saveTemplate(draft)
      setDrafts(current => ({ ...current, [draftId]: saved }))
      setDirty(current => ({ ...current, [draftId]: false }))
      notify?.(t('communications.templates.saved'), 'success')
    } catch (error) {
      notify?.(apiErrorMessage(error, t('communications.templates.saveFailed')), 'error')
    } finally {
      setBusy('')
    }
  }

  const reset = async () => {
    if (!draft || !window.confirm(t('communications.templates.resetConfirm'))) return
    setBusy('reset')
    try {
      const restored = await communicationsService.resetTemplate(selectedKey, locale)
      setDrafts(current => ({ ...current, [draftId]: restored }))
      setDirty(current => ({ ...current, [draftId]: false }))
      notify?.(t('communications.templates.resetDone'), 'success')
    } catch (error) {
      notify?.(apiErrorMessage(error, t('communications.templates.resetFailed')), 'error')
    } finally {
      setBusy('')
    }
  }

  const test = async () => {
    if (!draft) return
    if (!emailIsValid(recipient)) {
      notify?.(t('communications.validation.email'), 'warning')
      return
    }
    setBusy('test')
    try {
      await communicationsService.testTemplate(selectedKey, locale, recipient)
      notify?.(t('communications.templates.testSent'), 'success')
    } catch {
      notify?.(t('communications.templates.testFailed'), 'error')
    } finally {
      setBusy('')
    }
  }

  if (loading) return <div className="flex h-48 items-center justify-center"><Loader2 className="animate-spin text-blue-600" /></div>
  if (!draft) return <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-sm text-gray-500">{t('communications.templates.empty')}</div>

  return <div className="grid min-h-[640px] grid-cols-1 gap-4 xl:grid-cols-[240px_minmax(0,1fr)]">
    <aside className="space-y-4 rounded-xl border border-gray-200 bg-white p-2">
      {groups.map(group => <div key={group.category}>
        <div className="px-2 py-1.5 text-[10px] font-bold uppercase tracking-wide text-gray-400">
          {t(`communications.categories.${group.category}`)}
        </div>
        <div className="space-y-1">
          {group.items.map(item => <button key={item.templateKey} onClick={() => setSelectedKey(item.templateKey)}
            className={`w-full rounded-lg px-3 py-2 text-left ${
              selectedKey === item.templateKey ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'
            }`}>
            <span className="block text-xs font-semibold">{item.displayName}</span>
            {!item.isEnabled && <span className="mt-0.5 block text-[9px] font-bold uppercase text-amber-600">
              {t('communications.templates.disabled')}
            </span>}
          </button>)}
        </div>
      </div>)}
    </aside>

    <div className="min-w-0 space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-gray-200 bg-white p-3">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold">{draft.displayName}</h3>
          <p className="mt-0.5 text-[11px] text-gray-500">{draft.sentWhen}</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg bg-gray-100 p-1">
            {(['en', 'fr'] as Locale[]).map(item => <button key={item} onClick={() => setLocale(item)}
              className={`rounded-md px-3 py-1 text-[11px] font-bold uppercase ${
                locale === item ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500'
              }`}>{item}</button>)}
          </div>
          <label className="flex items-center gap-2 text-xs text-gray-600">
            <input type="checkbox" checked={draft.isEnabled} onChange={event => update('isEnabled', event.target.checked)}
              className="rounded border-gray-300 text-blue-600" />
            {t('communications.templates.enabled')}
          </label>
        </div>
      </header>

      <div className="grid grid-cols-1 gap-4 2xl:grid-cols-[minmax(0,1fr)_minmax(0,460px)]">
        <div className="min-w-0 space-y-3">
          <section className="space-y-3 rounded-xl border border-gray-200 bg-white p-3">
            <label className="block text-xs font-medium text-gray-600">
              {t('communications.templates.subject')}
              <input value={draft.subjectTemplate} onChange={event => update('subjectTemplate', event.target.value)}
                className="mt-1.5 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500" />
            </label>
            <label className="block text-xs font-medium text-gray-600">
              {t('communications.templates.heading')}
              <input value={draft.headingTemplate} onChange={event => update('headingTemplate', event.target.value)}
                className="mt-1.5 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500" />
            </label>
            <label className="block text-xs font-medium text-gray-600">
              {t('communications.templates.preheader')}
              <input value={draft.preheaderTemplate} onChange={event => update('preheaderTemplate', event.target.value)}
                className="mt-1.5 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500" />
              <span className="mt-1 block text-[10px] text-gray-500">{t('communications.templates.preheaderHint')}</span>
            </label>
            <label className="block text-xs font-medium text-gray-600">
              {t('communications.templates.actionLabel')}
              <input value={draft.actionLabelTemplate || ''} onChange={event => update('actionLabelTemplate', event.target.value)}
                placeholder={t('communications.templates.actionLabelEmpty')}
                className="mt-1.5 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500" />
            </label>
          </section>

          <section className="rounded-xl border border-gray-200 bg-white p-3">
            <h4 className="text-xs font-semibold text-gray-800">{t('communications.templates.layout')}</h4>
            <p className="mt-1 text-[10px] text-gray-500">{t('communications.templates.layoutHint')}</p>
            <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
              {layouts.map(layout => <button key={layout.key} onClick={() => update('layoutKey', layout.key)}
                className={`rounded-lg border p-2 text-left ${
                  draft.layoutKey === layout.key ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                }`}>
                <span className="block text-[11px] font-semibold text-gray-800">{layout.name}</span>
                <span className="mt-0.5 block text-[9px] leading-3 text-gray-500">{layout.description}</span>
              </button>)}
            </div>
          </section>

          <section className="rounded-xl border border-gray-200 bg-white p-3">
            <h4 className="text-xs font-semibold text-gray-800">{t('communications.templates.content')}</h4>
            <p className="mt-1 mb-3 text-[10px] text-gray-500">{t('communications.templates.contentHint')}</p>
            <MailBlockEditor blocks={draft.bodyBlocks} variables={variables}
              onChange={blocks => update('bodyBlocks', blocks)} />
          </section>
        </div>

        <div className="space-y-3">
          <section className="rounded-xl border border-gray-200 bg-white p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="text-[10px] font-bold uppercase text-gray-400">{t('communications.templates.previewSubject')}</div>
                <div className="truncate text-sm font-semibold">{preview?.subject || '—'}</div>
              </div>
              <div className="flex items-center gap-1">
                {previewing && <Loader2 size={13} className="mr-1 animate-spin text-blue-600" />}
                <button onClick={() => setPreviewMode(previewMode === 'html' ? 'text' : 'html')}
                  className={`rounded-md px-2 py-1 text-[10px] font-bold ${previewMode === 'text' ? 'bg-blue-50 text-blue-700' : 'text-gray-500'}`}>
                  {previewMode === 'html' ? 'TXT' : 'HTML'}
                </button>
                <button onClick={() => setDark(!dark)} title={t('communications.templates.toggleTheme')}
                  className={`rounded-md p-1.5 ${dark ? 'bg-slate-800 text-white' : 'text-gray-500'}`}>
                  {dark ? <Moon size={13} /> : <Sun size={13} />}
                </button>
                <button onClick={() => setMobile(false)} className={`rounded-md p-1.5 ${!mobile ? 'bg-blue-50 text-blue-700' : 'text-gray-500'}`}><Monitor size={13} /></button>
                <button onClick={() => setMobile(true)} className={`rounded-md p-1.5 ${mobile ? 'bg-blue-50 text-blue-700' : 'text-gray-500'}`}><Smartphone size={13} /></button>
              </div>
            </div>
            <div className={`mx-auto mt-3 overflow-hidden rounded-lg border border-gray-200 ${mobile ? 'max-w-[390px]' : 'max-w-full'}`}>
              {previewMode === 'html'
                ? <iframe title={t('communications.templates.preview')} srcDoc={preview?.html || ''} sandbox=""
                  className="h-[560px] w-full bg-white" />
                : <pre className="min-h-[300px] whitespace-pre-wrap bg-gray-50 p-4 text-xs text-gray-700">{preview?.text || ''}</pre>}
            </div>
          </section>

          <div className="flex flex-wrap justify-end gap-2">
            <button onClick={reset} disabled={Boolean(busy)}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 disabled:opacity-50">
              <RotateCcw size={13} />{t('communications.templates.reset')}
            </button>
            <button onClick={save} disabled={Boolean(busy) || !dirty[draftId]}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white disabled:opacity-50">
              {busy === 'save' ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
              {t('communications.templates.save')}
            </button>
          </div>

          <section className="rounded-xl border border-gray-200 bg-white p-3">
            <div className="flex items-center gap-2"><Send size={14} className="text-blue-600" />
              <h4 className="text-sm font-semibold">{t('communications.templates.sendTest')}</h4>
            </div>
            <p className="mt-1 text-[10px] text-gray-500">{t('communications.templates.testHint')}</p>
            <div className="mt-3 flex flex-col gap-2 sm:flex-row">
              <input list="template-recipients" type="email" value={recipient} onChange={event => setRecipient(event.target.value)}
                placeholder="recipient@example.com" className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm" />
              <datalist id="template-recipients">{recipientOptions.map(email => <option key={email} value={email} />)}</datalist>
              <button onClick={test} disabled={Boolean(busy) || Boolean(dirty[draftId])}
                title={dirty[draftId] ? t('communications.templates.saveBeforeTest') : undefined}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-slate-800 px-4 py-2 text-xs font-medium text-white disabled:opacity-50">
                {busy === 'test' ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                {t('communications.templates.send')}
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>
  </div>
}

export default EmailTemplateManager
